import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { projectService, clientService, employeeService } from "../services/apiService.jsx";
import "./Dashboard.css"; // or import a shared form CSS

const CreateProject = () => {
  const navigate = useNavigate();
  const formRef = useRef();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");

  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    project_name: "",
    customer_id: "",
    employee_id: "",
    project_start_date: "",
    project_end_date: "",
    project_type: "",
    project_description: "",
    project_priority: "Medium"
  });

  useEffect(() => {
    fetchFormData();
  }, []);

  const fetchFormData = async () => {
    setLoading(true);
    try {
      const [customersResponse, employeesResponse] = await Promise.all([
        clientService.getAllClients(),
        employeeService.getAllEmployees()
      ]);

      // Extract customers
      let customersData = [];
      if (customersResponse?.data) {
        customersData = Array.isArray(customersResponse.data)
          ? customersResponse.data
          : (customersResponse.data?.data && Array.isArray(customersResponse.data.data)
              ? customersResponse.data.data
              : []);
      }
      setCustomers(customersData);

      // Extract employees
      let employeesData = [];
      if (employeesResponse?.data?.data && Array.isArray(employeesResponse.data.data)) {
        employeesData = employeesResponse.data.data;
      }
      setEmployees(employeesData);
    } catch (error) {
      console.error("Error fetching form data:", error);
      showNotificationMessage("error", "Failed to load customers and employees. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const showNotificationMessage = (type, message) => {
    setNotificationType(type);
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.project_name?.trim()) {
      errors.project_name = "Project name is required";
    }
    if (formData.project_start_date && formData.project_end_date) {
      const start = new Date(formData.project_start_date);
      const end = new Date(formData.project_end_date);
      if (end < start) {
        errors.project_end_date = "End date cannot be before start date";
      }
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showNotificationMessage("error", "Please fix the validation errors");
      return;
    }

    setIsSubmitting(true);

    try {
      const projectData = {
        project_name: formData.project_name,
        customer_id: formData.customer_id ? parseInt(formData.customer_id) : null,
        employee_id: formData.employee_id ? parseInt(formData.employee_id) : null,
        project_start_date: formData.project_start_date || null,
        project_end_date: formData.project_end_date || null,
        project_type: formData.project_type || null,
        project_description: formData.project_description || null,
        project_priority: formData.project_priority || "Medium"
      };

      await projectService.createProject(projectData);
      showNotificationMessage("success", "Project created successfully!");
      setFormData({
        project_name: "",
        customer_id: "",
        employee_id: "",
        project_start_date: "",
        project_end_date: "",
        project_type: "",
        project_description: "",
        project_priority: "Medium"
      });
      formRef.current.reset();
      setTimeout(() => navigate("/projects"), 2000);
    } catch (error) {
      console.error("Error creating project:", error);
      let errorMsg = "Failed to create project";
      if (error.response) {
        if (error.response.status === 400) {
          const errorData = error.response.data;
          if (errorData.errors) {
            setValidationErrors(errorData.errors);
            showNotificationMessage("error", "Validation failed. Please check the form.");
            setIsSubmitting(false);
            return;
          } else {
            errorMsg = errorData.message || "Validation error";
          }
        } else if (error.response.status === 401) {
          errorMsg = "You are not authorized. Please login again.";
        } else if (error.response.status === 403) {
          errorMsg = "You don't have permission to create projects.";
        } else {
          errorMsg = error.response.data?.message || errorMsg;
        }
      } else if (error.request) {
        errorMsg = "No response from server. Please check your internet connection.";
      }
      showNotificationMessage("error", errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    "Software Development",
    "Web Development",
    "Mobile App Development",
    "UI/UX Design",
    "Quality Assurance",
    "DevOps",
    "Consulting",
    "Maintenance",
    "Research",
    "Other"
  ];

  const priorityOptions = ["High", "Medium", "Low"];

  return (
    <Layout>
      {/* Notification Popup */}
      {showNotification && (
        <div
          className={`notification-popup ${notificationType === 'success' ? 'notification-success' : 'notification-error'}`}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            padding: '12px 16px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: '280px',
            animation: 'slideIn 0.3s ease',
            fontSize: '0.9rem'
          }}
        >
          <i className={`bi ${notificationType === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`}></i>
          <span style={{ flex: 1 }}>{notificationMessage}</span>
          <button
            onClick={() => setShowNotification(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.1rem',
              cursor: 'pointer',
              padding: '0 3px'
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}

      {/* Header with back button */}
      <div className="d-flex align-items-center" style={{ marginBottom: '1rem' }}>
        <button
          onClick={() => navigate("/projects")}
          className="btn btn-link text-dark p-0 me-2"
          style={{ fontSize: '1.8rem', lineHeight: 1, textDecoration: 'none' }}
          title="Go back to projects list"
        >
          <i className="bi bi-arrow-left"></i>
        </button>
        <h2 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 500, color: 'var(--secondary)' }}>Create Project</h2>
      </div>

      {loading && (
        <div className="alert alert-info py-2 mb-3" style={{ fontSize: '0.9rem' }}>
          Loading customers and employees...
        </div>
      )}

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <form
                  id="projectForm"
                  ref={formRef}
                  noValidate
                  onSubmit={handleSubmit}
                >
                  <div className="row g-3">
                    {/* Project Name */}
                    <div className="col-md-3">
                      <label htmlFor="projectName" className="form-label">
                        Project Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="project_name"
                        className={`form-control form-control-sm ${validationErrors.project_name ? 'is-invalid' : ''}`}
                        id="projectName"
                        placeholder="Enter project name"
                        value={formData.project_name}
                        onChange={handleInputChange}
                        required
                      />
                      {validationErrors.project_name && (
                        <div className="invalid-feedback">{validationErrors.project_name}</div>
                      )}
                    </div>

                    {/* Customer Dropdown */}
                    <div className="col-md-3">
                      <label htmlFor="customerSelect" className="form-label">Customer</label>
                      <select
                        name="customer_id"
                        className={`form-select form-select-sm ${validationErrors.customer_id ? 'is-invalid' : ''}`}
                        id="customerSelect"
                        value={formData.customer_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Customer</option>
                        {customers.map((customer) => (
                          <option key={customer.customer_id || customer.id} value={customer.customer_id || customer.id}>
                            {customer.customer_name || customer.name || `Customer ${customer.customer_id}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Project Manager Dropdown */}
                    <div className="col-md-3">
                      <label htmlFor="employeeSelect" className="form-label">Project Manager</label>
                      <select
                        name="employee_id"
                        className={`form-select form-select-sm ${validationErrors.employee_id ? 'is-invalid' : ''}`}
                        id="employeeSelect"
                        value={formData.employee_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Employee</option>
                        {employees.map((emp) => (
                          <option key={emp.employee_id || emp.id} value={emp.employee_id || emp.id}>
                            {emp.first_name} {emp.last_name} - {emp.employee_code || ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Project Type */}
                    <div className="col-md-3">
                      <label htmlFor="projectType" className="form-label">Project Type</label>
                      <select
                        name="project_type"
                        className={`form-select form-select-sm ${validationErrors.project_type ? 'is-invalid' : ''}`}
                        id="projectType"
                        value={formData.project_type}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Type</option>
                        {projectTypes.map((type, idx) => (
                          <option key={idx} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    {/* Start Date */}
                    <div className="col-md-3">
                      <label htmlFor="startDate" className="form-label">Start Date</label>
                      <input
                        type="date"
                        name="project_start_date"
                        className={`form-control form-control-sm ${validationErrors.project_start_date ? 'is-invalid' : ''}`}
                        id="startDate"
                        value={formData.project_start_date}
                        onChange={handleInputChange}
                      />
                    </div>

                    {/* End Date */}
                    <div className="col-md-3">
                      <label htmlFor="endDate" className="form-label">End Date</label>
                      <input
                        type="date"
                        name="project_end_date"
                        className={`form-control form-control-sm ${validationErrors.project_end_date ? 'is-invalid' : ''}`}
                        id="endDate"
                        value={formData.project_end_date}
                        onChange={handleInputChange}
                      />
                      {validationErrors.project_end_date && (
                        <div className="invalid-feedback">{validationErrors.project_end_date}</div>
                      )}
                    </div>

                    {/* Priority */}
                    <div className="col-md-3">
                      <label htmlFor="priority" className="form-label">Priority</label>
                      <select
                        name="project_priority"
                        className={`form-select form-select-sm ${validationErrors.project_priority ? 'is-invalid' : ''}`}
                        id="priority"
                        value={formData.project_priority}
                        onChange={handleInputChange}
                      >
                        {priorityOptions.map((p, idx) => (
                          <option key={idx} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>

                    {/* Description */}
                    <div className="col-md-12">
                      <label htmlFor="description" className="form-label">Project Description</label>
                      <textarea
                        name="project_description"
                        className={`form-control form-control-sm ${validationErrors.project_description ? 'is-invalid' : ''}`}
                        id="description"
                        rows="2"
                        placeholder="Enter project description"
                        value={formData.project_description}
                        onChange={handleInputChange}
                      />
                    </div>

                    {/* Form Actions */}
                    <div className="col-12 d-flex justify-content-end gap-2 mt-3">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/projects")}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting || loading}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Creating...
                          </>
                        ) : (
                          "Create Project"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CreateProject;